// Lobby cat — sleeps next to the dreamers cabin entrance, breathes
// gently, occasionally rolls over. Pure ambient: no collision, no
// interaction, no AI. One sprite, 4 procedural canvas frames driving
// breath + a periodic 1.2s roll-over animation.

import * as THREE from 'three';

// Frame indices in the canvas atlas. The four frames are stacked
// horizontally on a single 64×16-px canvas (16px per frame, 16px tall).
//   0: curled-up sleeping (low breath)
//   1: curled-up sleeping (high breath, belly extended slightly)
//   2: mid roll-over (paws up, belly exposed)
//   3: stretched-out (legs extended, asleep on side)

type CatFrame = 0 | 1 | 2 | 3;

const BREATH_HZ = 0.5;
// Roll-over period — every ~22s the cat does a 1.6s roll cycle:
//   curled (0/1) → mid-roll (2) for 0.4s → stretched (3) hold 0.8s →
//   mid-roll (2) for 0.4s → back to curled.
const ROLL_INTERVAL_S = 22;
const ROLL_DURATION_S = 1.6;

export class LobbyCat {
  private sprite: THREE.Sprite;
  private mat: THREE.SpriteMaterial;
  private texByFrame: THREE.Texture[];
  private parent: THREE.Object3D;
  private currentFrame: CatFrame = 0;
  private spawnTSec: number;

  constructor(parent: THREE.Object3D, x: number, z: number, tSecAtSpawn = 0) {
    this.parent = parent;
    this.spawnTSec = tSecAtSpawn;
    this.texByFrame = [
      makeCatFrame(0),
      makeCatFrame(1),
      makeCatFrame(2),
      makeCatFrame(3),
    ];
    this.mat = new THREE.SpriteMaterial({
      map: this.texByFrame[0],
      transparent: true,
      depthWrite: false,
      toneMapped: false,
    });
    this.sprite = new THREE.Sprite(this.mat);
    // ~1.4u wide so the cat reads as a real lap-sized animal at the
    // lobby camera distance without dwarfing the player.
    this.sprite.scale.set(1.4, 1.0, 1);
    this.sprite.position.set(x, 0.45, z);
    this.parent.add(this.sprite);
  }

  update(tSec: number): void {
    const t = tSec - this.spawnTSec;
    // Roll-over schedule — once per ROLL_INTERVAL_S seconds.
    const rollPhase = t % ROLL_INTERVAL_S;
    let target: CatFrame;
    if (rollPhase < ROLL_DURATION_S) {
      // Inside the roll window — segment the 1.6s into 0.4 / 0.8 / 0.4.
      if (rollPhase < 0.4) target = 2;
      else if (rollPhase < 1.2) target = 3;
      else target = 2;
    } else {
      // Sleeping — alternate between low and high breath frames at BREATH_HZ.
      const breath = Math.sin(t * BREATH_HZ * Math.PI * 2) >= 0 ? 1 : 0;
      target = breath === 1 ? 1 : 0;
    }
    if (target !== this.currentFrame) {
      this.currentFrame = target;
      this.mat.map = this.texByFrame[target];
      this.mat.needsUpdate = true;
    }
  }

  dispose(): void {
    this.parent.remove(this.sprite);
    this.mat.dispose();
    for (const t of this.texByFrame) t.dispose();
  }
}

// -------------------------------------------------------------------
//   Procedural cat frame — 16×16, transparent bg. Tabby orange with
//   white chest. Each `frame` index is a different pose.
// -------------------------------------------------------------------

function makeCatFrame(frame: CatFrame): THREE.Texture {
  const SIZE = 16;
  const cnv = document.createElement('canvas');
  cnv.width = SIZE;
  cnv.height = SIZE;
  const ctx = cnv.getContext('2d')!;
  ctx.imageSmoothingEnabled = false;
  ctx.clearRect(0, 0, SIZE, SIZE);

  // Palette
  const FUR = '#d97706';
  const FUR_DK = '#92400e';
  const BELLY = '#fde68a';
  const OUTLINE = '#0b0d12';
  const PINK = '#fda4af';

  if (frame === 0 || frame === 1) {
    // Curled-up sleeping. Body = a single round mass with ears.
    const breath = frame === 1 ? 1 : 0;

    // Body — rounded blob.
    drawRect(ctx, 3, 8 - breath, 10, 6 + breath, FUR);
    // Tail curling over body.
    drawRect(ctx, 11, 7 - breath, 2, 1, FUR_DK);
    drawRect(ctx, 9, 6 - breath, 2, 1, FUR_DK);
    drawRect(ctx, 7, 6 - breath, 2, 1, FUR_DK);
    // Head tucked into body.
    drawRect(ctx, 3, 6, 4, 3, FUR);
    // Ears — two triangles on top of head.
    drawRect(ctx, 3, 5, 1, 1, FUR);
    drawRect(ctx, 5, 5, 1, 1, FUR);
    // Closed eyes — two thin dashes.
    drawRect(ctx, 3, 7, 1, 1, OUTLINE);
    drawRect(ctx, 5, 7, 1, 1, OUTLINE);
    // Tabby stripes.
    drawRect(ctx, 7, 9 - breath, 1, 1, FUR_DK);
    drawRect(ctx, 9, 10 - breath, 1, 1, FUR_DK);
    drawRect(ctx, 11, 9 - breath, 1, 1, FUR_DK);
    // Outline along bottom + outer edge.
    drawOutline(ctx, 3, 8 - breath, 10, 6 + breath, OUTLINE);
  } else if (frame === 2) {
    // Mid-roll — paws up, belly exposed.
    drawRect(ctx, 3, 8, 10, 5, FUR);
    drawRect(ctx, 4, 8, 8, 4, BELLY);
    // Paws sticking up.
    drawRect(ctx, 4, 6, 1, 2, FUR);
    drawRect(ctx, 6, 6, 1, 2, FUR);
    drawRect(ctx, 9, 6, 1, 2, FUR);
    drawRect(ctx, 11, 6, 1, 2, FUR);
    // Pink paw pads on top of paws.
    drawRect(ctx, 4, 6, 1, 1, PINK);
    drawRect(ctx, 6, 6, 1, 1, PINK);
    drawRect(ctx, 9, 6, 1, 1, PINK);
    drawRect(ctx, 11, 6, 1, 1, PINK);
    // Head turned to side.
    drawRect(ctx, 13, 9, 2, 3, FUR);
    drawRect(ctx, 13, 8, 1, 1, FUR); // ear
    drawRect(ctx, 14, 10, 1, 1, OUTLINE); // closed eye
    // Tabby stripes on belly fur.
    drawRect(ctx, 5, 9, 1, 1, FUR_DK);
    drawRect(ctx, 8, 9, 1, 1, FUR_DK);
    drawRect(ctx, 11, 9, 1, 1, FUR_DK);
    drawOutline(ctx, 3, 8, 10, 5, OUTLINE);
  } else {
    // Stretched-out side sleep. Body horizontal, legs extended.
    drawRect(ctx, 2, 9, 12, 4, FUR);
    drawRect(ctx, 3, 10, 10, 2, BELLY);
    // Tail trailing.
    drawRect(ctx, 14, 10, 1, 1, FUR);
    drawRect(ctx, 15, 11, 1, 1, FUR);
    // Head on the left.
    drawRect(ctx, 1, 8, 3, 3, FUR);
    drawRect(ctx, 1, 7, 1, 1, FUR); // ear
    drawRect(ctx, 3, 7, 1, 1, FUR); // ear
    drawRect(ctx, 2, 9, 1, 1, OUTLINE); // closed eye
    // Front legs extended.
    drawRect(ctx, 3, 13, 2, 1, FUR);
    drawRect(ctx, 6, 13, 2, 1, FUR);
    // Back legs.
    drawRect(ctx, 9, 13, 2, 1, FUR);
    drawRect(ctx, 12, 13, 2, 1, FUR);
    // Tabby stripes along back.
    drawRect(ctx, 5, 9, 1, 1, FUR_DK);
    drawRect(ctx, 8, 9, 1, 1, FUR_DK);
    drawRect(ctx, 11, 9, 1, 1, FUR_DK);
    drawOutline(ctx, 2, 9, 12, 4, OUTLINE);
  }

  const tex = new THREE.CanvasTexture(cnv);
  tex.magFilter = THREE.NearestFilter;
  tex.minFilter = THREE.NearestFilter;
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.needsUpdate = true;
  return tex;
}

function drawRect(
  ctx: CanvasRenderingContext2D,
  x: number, y: number, w: number, h: number, color: string,
): void {
  ctx.fillStyle = color;
  ctx.fillRect(x, y, w, h);
}

// 1px outline rectangle around the given bounds.
function drawOutline(
  ctx: CanvasRenderingContext2D,
  x: number, y: number, w: number, h: number, color: string,
): void {
  ctx.fillStyle = color;
  // top + bottom
  for (let dx = 0; dx < w; dx++) {
    ctx.fillRect(x + dx, y - 1, 1, 1);
    ctx.fillRect(x + dx, y + h, 1, 1);
  }
  // left + right
  for (let dy = 0; dy < h; dy++) {
    ctx.fillRect(x - 1, y + dy, 1, 1);
    ctx.fillRect(x + w, y + dy, 1, 1);
  }
}
